import http from '../utilities/http';
import config from '../config';

const requests = {
  del: (url) => http.delete(url),
  get: (url, query = 0) => http.get(url, { params: query }),
  put: (url, body) => http.put(url, body),
  post: (url, body) => http.post(url, body),
};

const Auth = {
  current: (payload) => requests.get(config.endpoints.auth.current, payload),
  login: (email, password) =>
    requests.post(config.endpoints.auth.login, { user: { email, password } }),
  register: (username, email, password) =>
    requests.post('/users', { user: { username, email, password } }),
  changePassword: (payload) => requests.put(config.endpoints.auth.changePassword, payload),
  forgotPassword: (payload) => requests.post(config.endpoints.auth.forgotPassword, payload),
  updateUser: (payload) => requests.put(config.endpoints.auth.users, payload),
  nonceMetaMask: (data) => requests.get(`${config.endpoints.auth.metamask}?public_address=${data}`),
  authMetaMask: (payload) => requests.post(config.endpoints.auth.authMetaMask, payload),
  getGoogleToken: (payload) => requests.post(config.endpoints.auth.googleToken, payload),
};

const Clients = {
  get: (query) => requests.get(config.endpoints.clients.get, query),
};

const Defi = {
  Accounts: {
    v2: {
      get: (query) => requests.get(config.endpoints.accounts.v2.get, query),
    },
    get: (query) => requests.get(config.endpoints.accounts.defi, query),
    getOne: (id) => requests.get(`${config.endpoints.accounts.create}/${id}`),
    create: (account) => requests.post(config.endpoints.accounts.create, account),
    update: ({ id, data }) => requests.put(`${config.endpoints.accounts.create}/${id}`, data),
    delete: (id) => requests.del(`${config.endpoints.accounts.defi}/${id}`),
  },

  Assets: {
    get: (payload) => requests.get(`${config.endpoints.defi.assets}/${payload.wallet}`, payload),
  },

  Balance: {
    get: (query) => requests.get(config.endpoints.defi.balances, query),
    getByAccount: ({ wallet }) => requests.get(`${config.endpoints.defi.balances}/${wallet}`),
  },

  Transactions: {
    get: (query) => requests.get(config.endpoints.defi.transactions, query),
    getDashboardTransactions: (query) =>
      requests.get(config.endpoints.defi.dashboardTransactions, query),
    getOne: (id) => requests.get(`${config.endpoints.defi.transaction}/${id}`),
    getByAccount: (account, query) =>
      requests.get(`${config.endpoints.defi.transactions}/${account}`, query),
  },

  Overview: {
    get: (query) => requests.get(config.endpoints.defi.overview, query),
    chain: (query) => requests.get(`${config.endpoints.defi.chainInfo}`, query),
    walletChain: ({ account, ...query }) =>
      requests.get(`${config.endpoints.defi.chainInfo}/${account}`, query),
    getOne: (id) => requests.get(`${config.endpoints.defi.overview}/${id}`),
    getSubpagesOverview: (wallet_address) =>
      requests.get(`${config.endpoints.defi.subpagesOverview}/${wallet_address}`),
    performanceSummary: (query) =>
      requests.get(`${config.endpoints.defi.performanceSummary}`, query),
  },

  Chains: {
    getOne: (id) => requests.get(`${config.endpoints.defi.chains}/${id}`),
    getInfo: (payload) =>
      requests.get(`${config.endpoints.defi.chainInfo}/${payload.wallet}`, payload),
  },

  QuarterlyPerformance: {
    get: (query) => requests.get(config.endpoints.defi.quarterlyPerformance, query),
    ofWallet: ({ wallet, ...query }) =>
      requests.get(`${config.endpoints.defi.quarterlyPerformance}/${wallet}`, query),
  },

  performanceSummaryByAccount: {
    get: (id) => requests.get(`${config.endpoints.defi.performanceSummaryByAccount}/${id}`),
  },
};

const Protocols = {
  get: (query) => requests.get(`${config.endpoints.protocols.get}`, query),
  ofAccount: (account, query) =>
    requests.get(`${config.endpoints.protocols.get}/${account}`, query),
};

const Snapshots = {
  get: (query) => requests.get(`${config.endpoints.snapshots.get}`, query),
};

const Notes = {
  create: ({ note }) => requests.post(`${config.endpoints.notes.create}`, note),
  update: ({ note, id }) => requests.put(`${config.endpoints.notes.create}/${id}`, note),
  transaction: (data) => requests.post(`${config.endpoints.notes.create}`, data),
};

const HedgeFund = {
  get: (query) => requests.get(config.endpoints.hedgeFunds.get, query),
  getHedgeFundStandardUsers: (users) =>
    requests.get(`${config.endpoints.hedgeFunds.getStandardUsers}/users`, users),
  getStandardUsers: (email) =>
    requests.get(`${config.endpoints.hedgeFunds.getStandardUsers}/${email}/users`),
  getHedgeFundManagers: (email) =>
    requests.get(`${config.endpoints.hedgeFunds.getHedgeFundManagers}/${email}/managers`),
  getOne: (id) => requests.get(`${config.endpoints.users.get}/${id}`),
  create: (hedgeFund) => requests.post(config.endpoints.users.get, hedgeFund),
  update: ({ id, data }) => requests.put(`${config.endpoints.users.get}/${id}`, data),
  delete: (id) => requests.del(`${config.endpoints.users.get}/${id}`),
};

const standardUser = {
  get: (query) => requests.get(config.endpoints.standardUser.get, query),
  getOne: (id) => requests.get(`${config.endpoints.users.get}/${id}`),
  create: (standardUser) => requests.post(config.endpoints.users.get, standardUser),
  update: ({ id, notes }) => requests.put(`${config.endpoints.users.get}/${id}`, { notes }),
  updateUser: ({ id, user }) => requests.put(`${config.endpoints.users.get}/${id}`, user),
  delete: (id) => requests.del(`${config.endpoints.users.get}/${id}`),
  updateStatus: ({ id, status }) => requests.put(`${config.endpoints.users.update}/${id}`, status),
};

const subscriptionPlan = {
  get: (query) => requests.get(config.endpoints.plan.get, query),
  getOne: (id) => requests.get(`${config.endpoints.plan.get}/${id}`),
  create: (plan) => requests.post(config.endpoints.plan.get, plan),
  update: ({ id, notes }) => requests.put(`${config.endpoints.plan.get}/${id}`, { notes }),
  updatePlan: ({ id, plan }) => requests.put(`${config.endpoints.plan.get}/${id}`, plan),
  delete: (id) => requests.del(`${config.endpoints.plan.get}/${id}`),
  updateStatus: ({ id, status }) => requests.put(`${config.endpoints.plan.update}/${id}`, status),
};

const subStandardUser = {
  getSubUsers: (query) => requests.get(config.endpoints.subStandardUser.getSubUsers, query),
  create: (standardUser) =>
    requests.post(config.endpoints.subStandardUser.getSubUsers, standardUser),
  updateSubUsers: ({ id, status }) =>
    requests.put(`${config.endpoints.subStandardUser.getSubUsers}/${id}`, { status }),
  deleteSubUser: (id) => requests.del(`${config.endpoints.subStandardUser.getSubUsers}/${id}`),
};

const subStandardUserTeam = {
  getSubUsersTeam: (query) =>
    requests.get(config.endpoints.subStandardUserTeam.getSubUsersTeam, query),
  create: (standardUser) =>
    requests.post(config.endpoints.subStandardUserTeam.getSubUsersTeam, standardUser),
  updateSubUsersTeam: ({ id, name }) =>
    requests.put(`${config.endpoints.subStandardUserTeam.getSubUsersTeam}/${id}`, { name }),
  deleteSubUserTeam: (id) =>
    requests.del(`${config.endpoints.subStandardUserTeam.getSubUsersTeam}/${id}`),
};

const superAdmin = {
  get: (query) => requests.get(config.endpoints.superadmin.get, query),
  getOne: (id) => requests.get(`${config.endpoints.users.get}/${id}`),
  create: (superAdmin) => requests.post(config.endpoints.users.get, superAdmin),
  update: ({ id, data }) => requests.put(`${config.endpoints.users.get}/${id}`, data),
  delete: (id) => requests.del(`${config.endpoints.users.get}/${id}`),
};

const Contract = {
  get: (query) => requests.get(config.endpoints.contract.get, query),
  getOne: (id) => requests.get(`${config.endpoints.contract.get}/${id}`),
  create: (contract) => requests.post(config.endpoints.contract.get, contract),
  update: ({ id, data }) => requests.put(`${config.endpoints.contract.get}/${id}`, data),
  delete: (id) => requests.del(`${config.endpoints.contract.get}/${id}`),
};

const Contact = {
  get: (query) => requests.get(config.endpoints.contact.get, query),
  getOne: (id) => requests.get(`${config.endpoints.contact.get}/${id}`),
  create: (contact) => requests.post(config.endpoints.contact.get, contact),
  update: ({ id, data }) => requests.put(`${config.endpoints.contact.get}/${id}`, data),
  delete: (id) => requests.del(`${config.endpoints.contact.get}/${id}`),
};

const Network = {
  get: () => requests.get(config.endpoints.network.get),
  update: (data) => requests.post(config.endpoints.network.update, data),
};

const Profile = {
  get: (query) => requests.get(config.endpoints.profile.get, query),
};

const Home = {
  Overview: {
    quarterlyPerformance: (query) =>
      requests.get(config.endpoints.user.quarterlyPerformance, query),
    performanceSummary: (query) =>
      requests.get(`${config.endpoints.user.performanceSummary}`, query),
  },
};

const Exchange = {
  create: (exchange) => requests.post(config.endpoints.accounts.create, exchange),
  update: ({ id, data }) => requests.put(`${config.endpoints.accounts.create}/${id}`, data),
  delete: (id) => requests.del(`${config.endpoints.exchange.get}/${id}`),

  SpotBalance: {
    get: (query) => requests.get(config.endpoints.exchange.spotBalances, query),
    getOne: (id) => requests.get(`${config.endpoints.exchange.spotBalances}/${id}`),
    getByAccount: (account, query) =>
      requests.get(`${config.endpoints.exchange.spotBalances}/${account}`, query),
  },

  FutureBalance: {
    get: (query) => requests.get(config.endpoints.exchange.futureBalances, query),
    getOne: (id) => requests.get(`${config.endpoints.exchange.futureBalances}/${id}`),
    getByAccount: (account, query) =>
      requests.get(`${config.endpoints.exchange.futureBalances}/${account}`, query),
  },

  Account: {
    get: (query) => requests.get(config.endpoints.exchange.accounts, query),
    getOne: (id) => requests.get(`${config.endpoints.exchange.accounts}/${id}`),
    create: (account) => requests.post(config.endpoints.accounts.create, account),
    update: ({ id, data }) => requests.put(`${config.endpoints.accounts.create}/${id}`, data),
    delete: ({ id, data }) => requests.del(`${config.endpoints.accounts.delete}/${id}`, data),
    updateStatus: ({ id, status }) =>
      requests.put(`${config.endpoints.accounts.update}/${id}`, status),
    enable: ({ id, data }) => requests.put(`${config.endpoints.accounts.update}/${id}`, data),
  },

  Transactions: {
    get: (query) => requests.get(config.endpoints.exchange.transactions, query),
    getOne: (id) => requests.get(`${config.endpoints.exchange.transactions}/${id}`),
    getByAccount: (account, query) =>
      requests.get(`${config.endpoints.exchange.transactionHistory}/${account}`, query),
  },

  Overview: {
    get: (query) => requests.get(config.endpoints.exchange.overview, query),
    assetInfo: (query) => requests.get(`${config.endpoints.exchange.assetInfo}`, query),
    accountAssetInfo: (account_id) =>
      requests.get(`${config.endpoints.exchange.accountAssetInfo}/${account_id}`),
    walletChain: ({ account, ...query }) =>
      requests.get(`${config.endpoints.exchange.chainInfo}/${account}`, query),
    getOne: (id) => requests.get(`${config.endpoints.exchange.overview}/${id}`),
    getSubpagesOverview: (wallet_address) =>
      requests.get(`${config.endpoints.exchange.subpagesOverview}/${wallet_address}`),
    performanceSummary: (query) =>
      requests.get(`${config.endpoints.exchange.performanceSummary}`, query),
  },

  Chains: {
    getOne: (id) => requests.get(`${config.endpoints.exchange.chains}/${id}`),
    getInfo: (payload) =>
      requests.get(`${config.endpoints.exchange.chainInfo}/${payload.wallet}`, payload),
  },

  QuarterlyPerformance: {
    get: (query) => requests.get(config.endpoints.exchange.quarterlyPerformance, query),
    getOne: (id) => requests.get(`${config.endpoints.exchange.quarterlyPerformance}/${id}`),
    ofWallet: ({ wallet, ...query }) =>
      requests.get(`${config.endpoints.exchange.quarterlyPerformance}/${wallet}`, query),
  },

  performanceSummaryByAccount: {
    get: (id) => requests.get(`${config.endpoints.exchange.performanceSummaryByAccount}/${id}`),
  },
};

const Agent = {
  Auth,
  Clients,
  Defi,
  Protocols,
  Notes,
  Contact,
  HedgeFund,
  standardUser,
  subscriptionPlan,
  subStandardUser,
  subStandardUserTeam,
  Contract,
  superAdmin,
  Snapshots,
  Exchange,
  Home,
  Network,
  Profile,
  // setToken: _token => { token = _token; }
};

export default Agent;
